import {
  type CategoryResponse,
  type CategorySortField,
  deleteCategory,
  type SortDirection,
} from '@api/categories-api';
import Breadcrumbs from '@components/breadcrumbs';
import ConfirmationModal from '@components/confirmation-modal';
import TableFilters from '@components/table-filters';
import { useAuth } from '@hooks/use-auth';
import { useCountries } from '@hooks/use-countries';
import { type CategoryFilters, usePaginatedCategories } from '@hooks/use-paginated-categories';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { sileo } from 'sileo';

import CreateCategoryModal from './create-category-modal';
import EditCategoryModal from './edit-category-modal';
import ImportCsvModal from './import-csv-modal';

const VISIBLE_DETAILS = 3;
const PAGE_SIZE = 21;
const DEBOUNCE_MS = 300;

function getPageRange(currentPage: number, totalPages: number): number[] {
  const MAX_VISIBLE = 7;

  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: number[] = [1];

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 5;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
    end = totalPages - 1;
  }

  if (start > 2) pages.push(-1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push(-2);

  pages.push(totalPages);
  return pages;
}

function getPrimaryName(cat: CategoryResponse): string {
  return cat.name;
}

interface CategoryCardProps {
  category: CategoryResponse;
  countries: { code: string; locale: string }[];
  onEdit: (category: CategoryResponse) => void;
  onDelete: (category: CategoryResponse) => void;
  canEdit: boolean;
}

function CategoryCard({
  category,
  countries,
  onEdit,
  onDelete,
  canEdit,
}: CategoryCardProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hasMore = category.details.length > VISIBLE_DETAILS;
  const visibleDetails = expanded ? category.details : category.details.slice(0, VISIBLE_DETAILS);
  const hiddenCount = category.details.length - VISIBLE_DETAILS;

  return (
    <div className="category-card">
      {category.externalId != null && (
        <button
          type="button"
          className="category-card__bookmark"
          title="Click to copy CSV ID"
          onClick={() => void navigator.clipboard.writeText(String(category.externalId))}
        >
          <span className="category-card__bookmark__label">ID</span>
          <span className="category-card__bookmark__value">{category.externalId}</span>
        </button>
      )}
      <div className="category-card__header">
        <div className="category-card__title-row">
          <h3 className="category-card__title">{getPrimaryName(category)}</h3>
          {category.isClinic && <span className="badge badge-clinic">Clinic</span>}
          {category.isGlobal && <span className="badge badge-global">Global</span>}
        </div>
      </div>

      {category.details.length > 0 && (
        <div className="category-card__body">
          <ul className="category-card__translations">
            {visibleDetails.map((d) => {
              const locale = countries.find((c) => c.code === d.country.code)?.locale;
              return (
                <li key={d.id} className="category-card__row">
                  <span className="badge badge-draft category-card__lang">
                    {locale && (
                      <img src={`/flags/${locale}.svg`} alt="" className="country-cell__flag" />
                    )}
                    {d.country.name}
                  </span>
                  <span className="category-card__name">{d.name}</span>
                </li>
              );
            })}
          </ul>
          {hasMore && (
            <button
              type="button"
              className="btn btn-ghost btn-sm category-card__toggle"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <ChevronUp size={14} />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  {hiddenCount} more detail{hiddenCount > 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      )}

      {canEdit && (
        <div className="category-card__edit-overlay">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onEdit(category)}
          >
            <Pencil size={14} />
            Edit category
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => onDelete(category)}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function ManageCategories(): React.JSX.Element {
  const { isAdmin } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<CategorySortField>('name');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [isClinicFilter, setIsClinicFilter] = useState<boolean | undefined>(undefined);
  const [isGlobalFilter, setIsGlobalFilter] = useState<boolean | undefined>(undefined);
  const [countryIdFilter, setCountryIdFilter] = useState<string | undefined>(undefined);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryResponse | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<CategoryResponse | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { countries } = useCountries();

  const filters: CategoryFilters = {
    search: debouncedSearch,
    sortBy,
    sortDir,
    isClinic: isClinicFilter,
    isGlobal: isGlobalFilter,
    countryId: countryIdFilter,
  };

  const { categories, meta, isLoading, error, refetch } = usePaginatedCategories(
    currentPage,
    PAGE_SIZE,
    filters,
  );

  const hasActiveFilters =
    isClinicFilter !== undefined || isGlobalFilter !== undefined || countryIdFilter !== undefined;

  const handleSortToggle = (field: string): void => {
    const sortField = field as CategorySortField;
    if (sortBy === sortField) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(sortField);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const toggleClinicFilter = (): void => {
    setIsClinicFilter((prev) => (prev === true ? undefined : true));
    setCurrentPage(1);
  };

  const toggleGlobalFilter = (): void => {
    setIsGlobalFilter((prev) => (prev === true ? undefined : true));
    setCurrentPage(1);
  };

  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const selectedCountry = countries.find((c) => c.id === countryIdFilter);

  useEffect(() => {
    if (!isCountryDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent): void => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCountryDropdownOpen]);

  const clearFilters = (): void => {
    setIsClinicFilter(undefined);
    setIsGlobalFilter(undefined);
    setCountryIdFilter(undefined);
    setCurrentPage(1);
  };

  const totalPages = meta?.totalPages ?? 1;

  const handleSearchChange = (value: string): void => {
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value.trim());
      setCurrentPage(1);
    }, DEBOUNCE_MS);
  };

  const goToPage = (page: number | ((prev: number) => number)): void => {
    setCurrentPage((prev) => {
      const next = typeof page === 'function' ? page(prev) : page;
      return Math.max(1, Math.min(next, totalPages));
    });
  };

  const handleCreateClose = (): void => {
    setIsCreateOpen(false);
    refetch();
  };

  const handleImportClose = (): void => {
    setIsImportOpen(false);
    refetch();
  };

  const handleEditClose = (): void => {
    setEditingCategory(null);
    refetch();
  };

  const handleDeleteCategory = async (category: CategoryResponse): Promise<void> => {
    try {
      await deleteCategory(category.id);
      sileo.success({ title: 'Category deleted successfully' });
      setDeletingCategory(null);
      refetch();
    } catch {
      sileo.error({ title: 'Failed to delete category' });
    }
  };

  if (isLoading && !meta) {
    return (
      <>
        <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: 'Categories' }]} />
        <div className="page-header">
          <div className="page-header__left">
            <h1 className="page-header__title">Categories</h1>
            <p className="page-header__subtitle">Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: 'Categories' }]} />
        <div className="page-error" role="alert">
          <span>Failed to load categories</span>
        </div>
      </>
    );
  }

  const startIndex = meta ? (meta.page - 1) * meta.pageSize + 1 : 0;
  const endIndex = meta ? Math.min(meta.page * meta.pageSize, meta.totalItems) : 0;

  return (
    <>
      <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: 'Categories' }]} />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">Categories</h1>
          <p className="page-header__subtitle">{meta?.totalItems ?? 0} categories registered</p>
        </div>
        <div className="page-header__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsImportOpen(true)}
            data-tooltip="Upload a CSV file to create or update categories in bulk"
          >
            <Upload size={16} />
            Import CSV
          </button>
          {isAdmin && (
            <button type="button" className="btn btn-primary" onClick={() => setIsCreateOpen(true)}>
              <Plus size={16} />
              New category
            </button>
          )}
        </div>
      </div>

      <TableFilters
        compact
        rows={[
          {
            items: [
              {
                type: 'search',
                id: 'category-search',
                label: 'Search',
                placeholder: 'Search categories...',
                value: searchInput,
                onChange: handleSearchChange,
              },
              {
                type: 'sort',
                activeField: sortBy,
                direction: sortDir,
                buttons: [
                  { field: 'name', label: 'Name' },
                  { field: 'externalId', label: 'ID' },
                ],
                onToggle: handleSortToggle,
              },
              {
                type: 'custom',
                content: (
                  <div className="toggle-group">
                    <button
                      type="button"
                      className={`toggle${isClinicFilter === true ? ' toggle--active' : ''}`}
                      role="switch"
                      aria-checked={isClinicFilter === true}
                      aria-label="Filter clinic categories"
                      onClick={toggleClinicFilter}
                    >
                      <span className="toggle__thumb" />
                    </button>
                    <span className="toggle__label">Clinic</span>
                  </div>
                ),
              },
              {
                type: 'custom',
                content: (
                  <div className="toggle-group">
                    <button
                      type="button"
                      className={`toggle${isGlobalFilter === true ? ' toggle--active' : ''}`}
                      role="switch"
                      aria-checked={isGlobalFilter === true}
                      aria-label="Filter global categories"
                      onClick={toggleGlobalFilter}
                    >
                      <span className="toggle__thumb" />
                    </button>
                    <span className="toggle__label">Global</span>
                  </div>
                ),
              },
              {
                type: 'custom',
                content: (
                  <div ref={countryDropdownRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className={`btn btn-sm ${countryIdFilter ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setIsCountryDropdownOpen((prev) => !prev)}
                      style={{ gap: 'var(--space-1)' }}
                    >
                      {selectedCountry ? (
                        <>
                          <img
                            src={`/flags/${selectedCountry.locale}.svg`}
                            alt=""
                            className="country-cell__flag"
                          />
                          {selectedCountry.name}
                        </>
                      ) : (
                        'All countries'
                      )}
                      <ChevronDown size={14} />
                    </button>

                    {isCountryDropdownOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: 4,
                          background: 'var(--color-bg-white)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-md)',
                          boxShadow: 'var(--shadow-md)',
                          zIndex: 50,
                          padding: 'var(--space-2)',
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            borderRadius: 'var(--radius-sm)',
                            padding: '0.5rem 0.75rem',
                            fontWeight: countryIdFilter === undefined ? 600 : 400,
                            marginBottom: 'var(--space-1)',
                          }}
                          onClick={() => {
                            setCountryIdFilter(undefined);
                            setIsCountryDropdownOpen(false);
                            setCurrentPage(1);
                          }}
                        >
                          All countries
                        </button>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 'var(--space-1)',
                          }}
                        >
                          {countries.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="btn btn-ghost"
                              style={{
                                justifyContent: 'flex-start',
                                borderRadius: 'var(--radius-sm)',
                                padding: '0.4rem 0.6rem',
                                gap: 'var(--space-1)',
                                fontWeight: countryIdFilter === c.id ? 600 : 400,
                                whiteSpace: 'nowrap',
                              }}
                              onClick={() => {
                                setCountryIdFilter(c.id);
                                setIsCountryDropdownOpen(false);
                                setCurrentPage(1);
                              }}
                            >
                              <img
                                src={`/flags/${c.locale}.svg`}
                                alt=""
                                className="country-cell__flag"
                              />
                              {c.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ),
              },
            ],
          },
        ]}
        onClear={clearFilters}
        showClear={hasActiveFilters}
      />

      {categories.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--space-12) var(--space-4)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {debouncedSearch ? 'No categories match your search' : 'No categories found'}
        </div>
      ) : (
        <>
          <div className="category-card-grid">
            {categories.map((cat) => (
              <CategoryCard
                key={cat.id}
                category={cat}
                countries={countries}
                onEdit={setEditingCategory}
                onDelete={setDeletingCategory}
                canEdit={isAdmin}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <span className="pagination__info">
                {startIndex}&ndash;{endIndex} of {meta?.totalItems}
              </span>

              <div className="pagination__controls">
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  onClick={() => goToPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>

                {getPageRange(currentPage, totalPages).map((page) =>
                  page < 0 ? (
                    <span key={page} className="pagination__ellipsis">
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={`pagination__btn${page === currentPage ? ' pagination__btn--active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  onClick={() => goToPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {isCreateOpen && <CreateCategoryModal isOpen onClose={handleCreateClose} />}

      {editingCategory && (
        <EditCategoryModal isOpen onClose={handleEditClose} category={editingCategory} />
      )}

      {isImportOpen && <ImportCsvModal isOpen onClose={handleImportClose} />}

      <ConfirmationModal
        isOpen={deletingCategory !== null}
        onClose={() => setDeletingCategory(null)}
        onConfirm={() => {
          if (deletingCategory) return handleDeleteCategory(deletingCategory);
        }}
        title="Delete category"
        message={`Are you sure you want to delete "${deletingCategory?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </>
  );
}
